import { useTranslation } from 'react-i18next'
import { useLatestRemake, useRemakeById, useRemakeVersions } from '@/hooks/useRemake'
import { RemakeSection } from './RemakeSection'
import { ImageSlotsSection } from './ImageSlotsSection'
import { KeywordCoverage } from './KeywordCoverage'
import { BackendKeywords } from './BackendKeywords'
import { RemakeVersionSelector } from './RemakeVersionSelector'

interface RemakeTabContentProps {
  jobId: string
  marketplace: string
  remakeId?: string
  productId: string
}

export function RemakeTabContent({ jobId, marketplace, remakeId, productId }: RemakeTabContentProps) {
  const { t } = useTranslation()
  const { data: versions } = useRemakeVersions(jobId)
  const { data: latest } = useLatestRemake(remakeId ? undefined : jobId)
  const { data: specific } = useRemakeById(remakeId)

  const remake = remakeId ? specific : latest
  const activeRemakeId = remake?.id
  const hasResult = !!remake?.text_result && (
    remake.status === 'completed'
    || remake.status === 'validation_failed'
    || remake.status === 'verification_required'
  )
  const rawKeywords = remake?.text_result?.backend_keywords
  const rawReport = remake?.text_result?.keyword_report
  const keywords = rawKeywords && (rawKeywords.search_terms ?? '').trim() ? rawKeywords : undefined
  const keywordReport = rawReport && (
    (rawReport.short_tail?.length ?? 0) > 0
    || (rawReport.long_tail?.length ?? 0) > 0
    || (rawReport.in_backend?.length ?? 0) > 0
  ) ? rawReport : undefined

  return (
    <div className="space-y-8">
      {versions && (
        <RemakeVersionSelector
          versions={versions}
          currentRemakeId={activeRemakeId}
          productId={productId}
          auditJobId={jobId}
        />
      )}

      <section id="remake-text">
        <RemakeSection jobId={jobId} marketplace={marketplace} />
      </section>

      {hasResult && (keywords || keywordReport) && (
        <section id="remake-keywords">
          <div className="border-t border-neutral-200 dark:border-neutral-700 pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {keywordReport && <KeywordCoverage report={keywordReport} />}
              {keywords && <BackendKeywords data={keywords} />}
            </div>
          </div>
        </section>
      )}

      {hasResult && activeRemakeId && (
        <section id="remake-images">
          <div className="border-t border-neutral-200 dark:border-neutral-700 pt-6">
            <h3 className="text-lg font-bold text-neutral-900 dark:text-neutral-100 mb-1">
              {t('amazonListing.concept.title')}
            </h3>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4">
              {t('amazonListing.concept.description')}
            </p>
            <ImageSlotsSection remakeId={activeRemakeId} />
          </div>
        </section>
      )}
    </div>
  )
}
